// CTSK-210 — tasko_greet: identity-only session introduction.
//
// Two-phase onboarding: a session announces its identity (this schema)
// BEFORE asking for capabilities (access-request path). Enables fleet
// visibility before any caps, cross-fleet discovery, silent-fleeting
// session tracking, and access-request identity de-duplication.
//
// Storage: reuses the External Clients Nest infrastructure (Option A
// per COO's spec). New doc tag `access/introduction` sits alongside
// existing `access/pending`/`access/approved`/`access/denied`. No new
// table, no new endpoint schema — just a new route + tag family.
//
// Idempotency: server keys on `session_key`. Second greet with the same
// session_key returns the ORIGINAL greetId + first_seen_utc, with
// `status: "existing"`. The introduction doc's last_greeted_utc +
// greet_count fields are updated on re-greet; no doc recreation.
//
// Wire convention: camelCase for the input schema (tool-API ergonomics);
// server projects to snake_case frontmatter keys internally.
import { z } from 'zod';
// ─── Enums ───────────────────────────────────────────────────────────
/** Where the session is running. Local = Cowork Claude Desktop on a
 *  user's machine (session_key shape `local_<uuid>`). Cloud = Anthropic
 *  Code cloud sandbox (session_key shape `cse_<opaque>`). Additional
 *  variants (e.g., dedicated tenant, self-hosted) can be added
 *  additively when they land. */
export const IntroductionVariantSchema = z.enum(['local', 'cloud']);
/** Severity level for a fleet-wide announcement returned in every greet
 *  response. `info` = routine notice, `warning` = advisory
 *  (deprecation, planned outage), `critical` = urgent (breaking change,
 *  auth invalidation). */
export const AnnouncementSeveritySchema = z.enum(['info', 'warning', 'critical']);
// ─── Input: tasko_greet ─────────────────────────────────────────────
/** Five required identity fields — the minimum the server needs to
 *  slot a session into the fleet map. All derivable from the v3
 *  environment probe (`Mission Control/env-probe-v3.md`) with zero
 *  ambiguity:
 *
 *   - session_key         → identity.id_hierarchy.session_key
 *   - variant             → identity.id_hierarchy.session_key.startsWith('cse_') ? 'cloud' : 'local'
 *   - account_uuid        → identity.id_hierarchy.account_uuid
 *   - organization_uuid   → identity.id_hierarchy.organization_uuid
 *   - friendly_name       → human-provided (sourceTag, e.g. "cpt-tasko-main")
 *
 *  No prose. No caps. No scope. No PAT metadata. Any of those live in
 *  the follow-on tasko_request_access call.
 *
 *  session_key MUST be canonical form:
 *   - LOCAL: `local_<uuid>` (e.g. "local_8e04b81b-756f-42a0-9ce6-0c1029c684c2")
 *   - CLOUD: `cse_<opaque>` (e.g. "cse_012r9TpRLUG8X3QQmzkCFHFu")
 *  Legacy bare-hex / hyphenated forms are NOT accepted here — greet is
 *  new surface and should hold clients to the canonical shape from day
 *  one.
 */
/**
 * CTSK-208 canonical session_key grammar. Single source of truth —
 * greet (this file), project session refs (project.ts / CTSK-146), and
 * any future session-identity surface must all reuse this constant
 * rather than re-typing the pattern.
 */
export const CANONICAL_SESSION_KEY_REGEX = /^(local_[0-9a-fA-F][0-9a-fA-F-]*|cse_[A-Za-z0-9_-]{8,})$/;
export const IntroductionMetadataSchema = z.object({
    /** Canonical session_key — the true dedup key. Idempotency uses this. */
    sessionKey: z.string().min(8).max(200).regex(CANONICAL_SESSION_KEY_REGEX, 'sessionKey must be `local_<uuid>` or `cse_<opaque>`'),
    /** local vs cloud. Server cross-checks against sessionKey prefix. */
    variant: IntroductionVariantSchema,
    /** Anthropic account UUID (invariant per account). */
    accountUuid: z.string().min(1).max(200),
    /** Anthropic organization UUID within the account. */
    organizationUuid: z.string().min(1).max(200),
    /** Human-readable identifier for the session. Not unique — duplicate
     *  friendly_names are allowed. */
    friendlyName: z.string().min(1).max(128),
});
// ─── Output: GreetResult ────────────────────────────────────────────
/** Aggregate view of the fleet at greet time. Snapshot only — does not
 *  bind to the session's future queries; the operator dashboard shows
 *  live values. Cheap fields (integer counts + short array of names). */
export const FleetPositionSchema = z.object({
    /** Total non-cancelled introductions across ALL fleets. */
    activeCount: z.number().int().nonnegative(),
    /** Introductions matching this session's (accountUuid, organizationUuid). */
    sameFleetCount: z.number().int().nonnegative(),
    /** Introductions on OTHER account+org combinations. */
    crossFleetCount: z.number().int().nonnegative(),
});
/** Fleet-wide announcement returned in every greet response. Read by
 *  the greet endpoint from the fixed-slug `intro-fleet-announcement`
 *  doc; omitted when that doc doesn't exist or has an empty body.
 *  Small (< 4KB body), no cache — small enough to read per-request. */
export const AnnouncementSchema = z.object({
    /** ISO 8601 UTC — when the operator last updated the announcement. */
    updatedUtc: z.string(),
    /** sourceTag of the operator who set the announcement. */
    updatedBy: z.string(),
    severity: AnnouncementSeveritySchema,
    /** Short headline (≤ 200 chars). */
    title: z.string().max(200),
    /** Markdown body (≤ 4KB). */
    body: z.string().max(4096),
    /** Optional URL for "read more" — points at a longer doc, CTSK ticket,
     *  or external write-up. */
    readMoreUrl: z.string().url().max(2048).optional(),
});
/** Full response envelope from POST /introductions. */
export const GreetResultSchema = z.object({
    /** ULID assigned at first-greet. Idempotent — same sessionKey always
     *  returns the same greetId across all subsequent greet calls. */
    greetId: z.string().min(26).max(26),
    /** `new` = doc created this call. `existing` = idempotent hit; the doc
     *  already existed for this sessionKey and was updated in place. */
    status: z.enum(['new', 'existing']),
    /** ISO 8601 UTC — when this session's introduction was first created.
     *  On `existing` this is the original greet time, not now. */
    firstSeenUtc: z.string(),
    fleetPosition: FleetPositionSchema,
    /** Top-N friendly_names from the SAME fleet (accountUuid + organizationUuid
     *  match). Bounded to a small N (≤ 5) so the response stays cheap.
     *  Sorted by lastGreetedUtc DESC — most recently-active peers first.
     *  Cross-fleet peers are NEVER included — fleet-boundary rule. */
    peersSample: z.array(z.string()).max(5),
    /** OPTIONAL — omitted when no active fleet-wide announcement is set. */
    announcement: AnnouncementSchema.optional(),
    /** Human-readable next-step hint. Static per-status; e.g. on `new`:
     *  "When ready for caps, call tasko_request_access(greetId, role, rationale)". */
    nextStep: z.string(),
});
// ─── Output: GET /introductions/resolve ────────────────────────────
/** CTSK-401 — which kind of doc a `/introductions/resolve` hit
 *  resolved to. `introduction` = a live `tasko_greet` doc (existing
 *  behavior). `access_request` = CTSK-401 fallback: the newest
 *  non-deleted access-request doc for this session_key, used when no
 *  introduction doc exists (e.g. pre-greet-adoption sessions). `null` =
 *  neither exists — normal state, not an error. */
export const IntroductionDocKindSchema = z.enum(['introduction', 'access_request']).nullable();
/** Full response envelope from GET /introductions/resolve. */
export const IntroductionResolveResponseSchema = z.object({
    sessionKey: z.string(),
    /** The resolved doc's id — an introduction doc, an access-request
     *  doc (CTSK-401 fallback), or null when neither exists. */
    documentId: z.string().nullable(),
    /** Which kind of doc `documentId` points at. Drives the SessionRefs
     *  UI's clickability + tooltip differentiation (CTSK-401). */
    docKind: IntroductionDocKindSchema,
    /** greetId, present only when docKind === 'introduction'. */
    greetId: z.string().nullable(),
    /** Human-readable label for the tooltip: intro_friendly_name for
     *  introduction docs, source_tag for access-request docs, null
     *  otherwise. */
    friendlyName: z.string().nullable(),
});
// ─── Constants ──────────────────────────────────────────────────────
/** Fixed slug for the operator's fleet-wide announcement doc. Read by
 *  the greet endpoint on every response. */
export const INTRO_ANNOUNCEMENT_DOC_SLUG = 'intro-fleet-announcement';
/** Peers_sample bound. Kept small to keep the response payload cheap
 *  and avoid inadvertently exposing the full fleet roster on high-
 *  membership orgs. */
export const INTRO_PEERS_SAMPLE_LIMIT = 5;
/** Frontmatter key names used for introduction docs. Kept in one place
 *  so the route + template + validator + external readers all agree.
 *  Snake-case on the wire; camelCase on the tool API. */
export const INTRODUCTION_FRONTMATTER_KEYS = {
    greetId: 'greet_id',
    sessionKey: 'session_key',
    variant: 'intro_variant',
    accountUuid: 'account_uuid',
    organizationUuid: 'organization_uuid',
    friendlyName: 'intro_friendly_name',
    firstSeenUtc: 'first_seen_utc',
    lastGreetedUtc: 'last_greeted_utc',
    greetCount: 'greet_count',
    createdBySourceTag: 'created_by_source_tag',
};
//# sourceMappingURL=introduction.js.map